import {ReactNode} from 'react';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {userProfileEndpoint} from '@/api-endpoints/endpoints';
import {PATHS} from '@/routes/paths';
import {UnauthorizedError} from '@/api-transport/errors';
import {signOut} from '@/auth/api';
import {AuthContext, type AuthContextType} from './context';

type AuthProviderProps = {
  children: ReactNode;
};

export function AuthProvider({children}: AuthProviderProps) {
  const queryClient = useQueryClient();

  const {
    data: isAuthorized,
    isLoading: isCheckAuthLoading,
    refetch: refetchCheckAuth,
    error: checkAuthError,
  } = useQuery({
    ...userProfileEndpoint,
  });

  const logout = async () => {
    try {
      await signOut();
    } catch {
      // ignore errors during logout — destroy the local session regardless
    }
    queryClient.clear();
    window.location.href = PATHS.login.to;
  };

  let status: AuthContextType['status'] = isCheckAuthLoading
    ? 'loading'
    : 'idle';

  if (isAuthorized) {
    status = 'authenticated';
  } else if (checkAuthError) {
    if (checkAuthError instanceof UnauthorizedError) {
      status = 'unauthenticated';
    } else {
      status = 'error';
    }
  } else if (!isCheckAuthLoading && isAuthorized === null) {
    status = 'unauthenticated';
  }

  let error = checkAuthError || null;

  const contextValue: AuthContextType = {
    status,
    error,
    logout,
    refetchCheckAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}
